import React, { useState, useEffect, useRef } from 'react';
import { createStyles } from '@mantine/core';

const useStyles = createStyles((theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    cursor: 'pointer',
    width: '100%',
    height: '100%',
  },

  imageContainer: {
    position: 'relative',
    cursor: 'default',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },

  imageWrapper: {
    position: 'relative',
    transition: 'transform 0.1s ease-out',
    willChange: 'transform',
  },

  image: {
    width: '50vw',
    height: 'auto',
    display: 'block',
    userDrag: 'none',
    userSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserDrag: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
    maxWidth: 'none',
    maxHeight: 'none',
  },

  info: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: theme.white,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '14px',
    maxWidth: '80%',
    textAlign: 'center',
  },

  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    border: 'none',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    transition: 'all 0.2s ease',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      transform: 'scale(1.1)',
    },
  },
}));

interface ImgShowProps {
  imageUrl: string;
  isShow: boolean;
  isclonse?: boolean;
  isBackground?: boolean;
  imageName?: string;
  onClose: () => void;
}

const ImgShow: React.FC<ImgShowProps> = ({
  imageUrl,
  isShow,
  isBackground,
  isclonse = false,
  imageName = '',
  onClose,
}) => {
  const { classes } = useStyles();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  // 完全重置状态
  const resetState = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    setStartPos({ x: 0, y: 0 });
  };

  // 当图片加载完成时
  const handleImageLoad = () => {
    resetState();
    setIsLoading(false);
  };

  // 当 isShow 变化时重置状态
  useEffect(() => {
    if (isShow) {
      setIsLoading(true);
      if (imageRef.current?.complete) {
        resetState();
        setIsLoading(false);
      }
    }
  }, [isShow]);

  // 处理图片加载
  useEffect(() => {
    if (!isShow) return;
    
    const img = new Image();
    img.src = imageUrl;
    img.onload = handleImageLoad;
    img.onerror = () => setIsLoading(false);

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, isShow]);

  // 处理点击外部关闭
  useEffect(() => {
    if (!isShow) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (imageContainerRef.current && !imageContainerRef.current.contains(e.target as Node)) {
        resetState();
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isShow, onClose]);

  // 处理键盘事件
  useEffect(() => {
    if (!isShow) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetState();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isShow, onClose]);

  // 处理鼠标滚轮缩放
  useEffect(() => {
    if (!isShow || !imageContainerRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prev => Math.min(Math.max(prev + delta, 0.1), 3));
    };

    imageContainerRef.current.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      imageContainerRef.current?.removeEventListener('wheel', handleWheel);
    };
  }, [isShow]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    animationRef.current = requestAnimationFrame(() => {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y,
      });
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetState();
    onClose();
  };

  if (!isShow) return null;

  return (
    <div className={classes.overlay}
        style={{
            backgroundColor: isBackground ? '' : 'rgba(0, 0, 0, 0.85)',
        }}
    >
      <div 
        ref={imageContainerRef}
        className={classes.imageContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className={classes.closeButton}
          onClick={handleClose}
        >
          ×
        </button>
        
        <div
          ref={imageWrapperRef}
          className={classes.imageWrapper}
          style={{
            transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
            //   点击当前北京 ，关闭弹窗
          onClick={handleClose}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt={imageName}
            className={classes.image}
            style={{
              opacity: isLoading ? 0 : 1,
            }}
            onLoad={handleImageLoad}
            onDragStart={handleDragStart}
          />
        </div>
        
        {imageName && (
          <div className={classes.info}>
            {imageName}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImgShow;